/**
 * "Show me" walks (ported from Sun Stellar 2026-10-02). A walk is the lesson's
 * own steps, shown one card at a time over the REAL page. Each step lists the
 * real element(s) it points at (`data-tour="…"` attributes on the pages; the
 * shell's own labels for the top bar and sidebar). A step whose element is not
 * on the page shows its card with no ring.
 *
 * A walk only points. It never types, never picks, and presses nothing except
 * the controls in TOUR_OPENERS — buttons that open a form so its fields can be
 * shown. No walk presses a control that saves or sends.
 *
 * A lesson has a "Show me" button only when it is listed here (the catalogue's
 * flag means "allowed", this list means "available").
 */
import { canShowMe, type TourLesson, type TourPlatform } from './tourCatalog';
import type { TourWalk } from './tourState';

export interface WalkStepTarget {
  /** Real element(s) to keep bright. Several selectors → one ring around all of them. */
  selector?: string;
  /** Page the step runs on. Missing → the page the walk is already on. */
  route?: string;
  /** Opener (from TOUR_OPENERS) pressed when the step's element is not on the page yet. */
  open?: string;
  /** The element is only there in some cases. */
  optional?: boolean;
}

const at = (...names: string[]) => names.map((name) => `[data-tour="${name}"]`).join(', ');
const SIDEBAR = '[data-aura-chrome="sidebar"]';
const ACCOUNT_MENU = at('account-menu');
const REPORT_ISSUE = at('resolution-report');
export const RESOLUTION_CENTRE = '/veda-agro/resolution-centre';

/** The only controls a walk may press. Each one opens a form (pressing it twice changes nothing); none saves or sends. */
export const TOUR_OPENERS: readonly string[] = [REPORT_ISSUE];

const onPage = (route: string, steps: WalkStepTarget[]): WalkStepTarget[] => steps.map((step) => ({ ...step, route }));

export const TOUR_WALKS: Record<string, WalkStepTarget[]> = {
  // Find your way around the web app — every target is in the shell, so it runs over the lesson page.
  'M01-L08': [
    { selector: SIDEBAR },
    { selector: at('notifications') },
    { selector: ACCOUNT_MENU },
    { selector: '[title="Dark mode"], [title="Light mode"], [title="Switch to compact"], [title="Switch to comfortable"]' },
  ],
  // Make a bug report and follow it (web) — stops before "Submit ticket".
  'M11-L06': [
    { selector: ACCOUNT_MENU },
    ...onPage(RESOLUTION_CENTRE, [
      { selector: at('resolution-module', 'resolution-urgency', 'resolution-description'), open: REPORT_ISSUE },
      // Points at "Submit ticket" — never presses it.
      { selector: at('resolution-submit'), open: REPORT_ISSUE },
      { selector: at('resolution-tabs') },
    ]),
  ],
};

/** True when this lesson has a walk here: the lesson allows it and it is wired above. */
export function hasWalk(lesson: TourLesson, platform: TourPlatform): boolean {
  return Boolean(TOUR_WALKS[lesson.id]) && canShowMe(lesson, platform);
}

/** The control the step names ("Report a bug"), else "Step n". */
function stepTitle(body: string, index: number): string {
  return body.match(/"([^"]+)"/)?.[1] ?? `Step ${index + 1}`;
}

export function walkFor(lesson: TourLesson): TourWalk {
  const targets = TOUR_WALKS[lesson.id] ?? [];
  const stop = lesson.showMe.stopBefore;
  // "Yes, stop before …": say so on the last step that names that button. The walk never presses it.
  const named = stop ? lesson.steps.map((body) => body.includes(stop)).lastIndexOf(true) : -1;
  const stopAt = !stop ? -1 : named >= 0 ? named : lesson.steps.length - 1;
  return {
    lessonId: lesson.id,
    lessonTitle: lesson.title,
    steps: lesson.steps.map((body, index) => {
      const target = targets[index];
      return {
        selector: target?.selector,
        route: target?.route,
        open: target?.open && TOUR_OPENERS.includes(target.open) ? target.open : undefined,
        title: stepTitle(body, index),
        body: index === stopAt ? `${body} The walk stops before ${stop}.` : body,
      };
    }),
  };
}
